import React, { useState, useEffect } from 'react';
import { getRequest } from '../services/apiService';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ReusableTable from '../dashboard/ReusableTable';
import SCurveChartData from './SCurveChartData';

export default function SCurveData() {
  const [projects, setProjects] = useState([]);
  const [ships, setShips] = useState([]);
  const [selectedProject, setSelectedProject] = useState('1');
  const [selectedShip, setSelectedShip] = useState('12');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [chartData, setChartData] = useState({
    contractual: [],
    revised: [],
    achieved: []
  });
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchProjects = async () => {
    try {
      const res = await getRequest('master/projects/');
      setProjects(res.results?.data || res.data || []);
    } catch {
      console.error('Failed to fetch projects.');
    }
  };

  const fetchShips = async (projectId) => {
    try {
      const res = await getRequest(`master/projects/?parent_id=${projectId}`);
      setShips(res.results?.data || res.data || []);
    } catch {
      console.error('Failed to fetch ships.');
    }
  };

  useEffect(() => {
    fetchProjects();
    fetchShips('1'); // Fetch ships for default project
  }, []);

  useEffect(() => {
    if (selectedProject) {
      fetchSCurveData();
    }
  }, [selectedProject, selectedShip, selectedYear]);

  const fetchSCurveData = async () => {
    setLoading(true);
    try {
      let url = `/transaction/api/project/${selectedProject}/s-curve/`;
      if (selectedShip) {
        url += `?ship_id=${selectedShip}`;
      }

      const res = await getRequest(url);
      
      const processData = (curveData) => {
        if (!curveData?.Summary) return [];
        return Object.entries(curveData.Summary)
          .map(([date, value]) => ({
            date,
            value: value || 0,
            year: date.split('-')[0],
            month: date.split('-')[1]
          }))
          .filter(item => item.date.startsWith(selectedYear))
          .sort((a, b) => a.date.localeCompare(b.date));
      };

      setChartData({
        contractual: processData(res["Contractual S-Curve"]),
        revised: processData(res["Revised S-Curve"]),
        achieved: processData(res["Achieved Progress %"])
      });

      // Get all unique dates from all three data sources
      const allDates = [
        ...Object.keys(res["Contractual S-Curve"]?.Summary || {}),
        ...Object.keys(res["Revised S-Curve"]?.Summary || {}),
        ...Object.keys(res["Achieved Progress %"]?.Summary || {})
      ].filter((value, index, self) => self.indexOf(value) === index);

      // Create table data with all dates
      const tableData = allDates
        .filter(date => date.startsWith(selectedYear))
        .map(date => ({
          date,
          contractual: res["Contractual S-Curve"]?.Summary[date] ?? '-',
          revised: res["Revised S-Curve"]?.Summary[date] ?? '-',
          achieved: res["Achieved Progress %"]?.Summary[date] ?? '-',
          year: date.split('-')[0],
          month: date.split('-')[1].padStart(2, '0')
        }))
        .sort((a, b) => a.date.localeCompare(b.date));

      setTableData(tableData);

    } catch (error) {
      console.error('Error fetching S-Curve data:', error);
      setChartData({ contractual: [], revised: [], achieved: [] });
      setTableData([]);
    } finally {
      setLoading(false);
    }
  };

  const getMonthName = (monthNumber) => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[parseInt(monthNumber) - 1] || 'Unknown';
  };

  const columnFields = [
    { field: 'date', header: 'Date', sortable: true },
    { field: 'month', header: 'Month', sortable: true, body: (rowData) => getMonthName(rowData.month) },
    { field: 'year', header: 'Year', sortable: true },
    { 
      field: 'contractual', 
      header: 'Contractual (%)', 
      sortable: true,
      body: (rowData) => rowData.contractual === '-' ? '-' : `${parseFloat(rowData.contractual).toFixed(2)}%`
    },
    { 
      field: 'revised', 
      header: 'Revised (%)', 
      sortable: true,
      body: (rowData) => rowData.revised === '-' ? '-' : `${parseFloat(rowData.revised).toFixed(2)}%`
    },
    { 
      field: 'achieved', 
      header: 'Achieved (%)', 
      sortable: true,
      body: (rowData) => rowData.achieved === '-' ? '-' : `${parseFloat(rowData.achieved).toFixed(2)}%`
    },
    // { 
    //   field: 'difference', 
    //   header: 'Difference', 
    //   sortable: true,
    //   body: (rowData) => {
    //     if (rowData.contractual === '-' || rowData.achieved === '-') return '-';
    //     const contractual = parseFloat(rowData.contractual);
    //     const achieved = parseFloat(rowData.achieved);
    //     const diff = (achieved - contractual).toFixed(2);
    //     const color = diff >= 0 ? 'text-green-500' : 'text-red-500';
    //     return <span className={color}>{diff}%</span>;
    //   }
    // }
  ];

  return (
    <div className="p-4">
      <ToastContainer />
      
      <div className="flex flex-wrap gap-4 mb-6 p-4 bg-white rounded-lg shadow">
        <div className="flex-1 min-w-[200px]">
          <label className="block text-sm font-bold text-gray-700 mb-1">Project:</label>
          <select
            value={selectedProject}
            onChange={(e) => {
              setSelectedProject(e.target.value);
              setSelectedShip('');
            }}
            className="w-full p-2 border border-gray-600 rounded-md"
          >
            {projects.map(project => (
              <option key={project.id} value={project.id}>{project.name}</option>
            ))}
          </select>
        </div>

        <div className="flex-1 min-w-[200px]">
          <label className="block text-sm font- texboldt-gray-700 mb-1">Ship:</label>
          <select
            value={selectedShip}
            onChange={(e) => setSelectedShip(e.target.value)}
            className="w-full p-2 border border-gray-600 rounded-md"
          >
            {ships.map(ship => (
              <option key={ship.id} value={ship.id}>{ship.name}</option>
            ))}
          </select>
        </div>

        <div className="flex-1 min-w-[150px]">
          <label className="block text-sm font-bold text-gray-700 mb-1">Year:</label>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            className="w-full p-2 border border-gray-600 rounded-md"
          >
            {Array.from({length: 10}, (_, i) => new Date().getFullYear() - i).map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="mb-6">
        <SCurveChartData 
          data={chartData} 
          selectedYear={selectedYear}
        />
      </div>

<ReusableTable
  title="S-Curve Data"
  data={tableData}
  columnFields={columnFields}
  loading={loading}
  selectionMode="single"
  paginator={tableData.length > 10}
  rows={10}
  hideSearchAndExport={false} // Keep exports visible
  hideHeader={false}
  // Add these two props to disable search:
  searchValue={undefined}
  onSearchChange={undefined}
/>
    </div>
  );
}